import { z } from 'zod';

import {
  HandoffDescriptionSchema,
  HandoffNameSchema,
  HandoffPhoneNumberSchema,
  OutboundEndpointSchema,
  SipHeaderKeySchema,
  SipHeaderValueSchema,
} from '@polyai/stream/commands/handlers/schemas/handoff';

import { customErrorMap } from './Error.uiCopy';
import { SelectOptionSchemaTransformed } from './shared';

z.setErrorMap(customErrorMap);

export const sipInvite = z.literal('invite');
export const sipRefer = z.literal('refer');
export const sipBye = z.literal('bye');

export type SipInvite = z.infer<typeof sipInvite>;

const sipMethod = z.union([sipInvite, sipRefer, sipBye]);

export type SipMethod = z.infer<typeof sipMethod>;

const IdSchema = z.object({
  id: z.string(),
});

const SipHeaderSchema = z.object({
  isLast: z.boolean(),
  key: SipHeaderKeySchema.optional(),
  value: SipHeaderValueSchema.optional(),
});

const SipHeadersSchema = z
  .array(
    z.discriminatedUnion('isLast', [
      SipHeaderSchema.merge(z.object({ isLast: z.literal(false) })),
      SipHeaderSchema.partial().merge(z.object({ isLast: z.literal(true) })),
    ]),
  )
  .min(1)
  .transform(headers =>
    headers
      .filter(header => !header.isLast)
      .reduce(
        (acc, header) => {
          if (header.key && header.value) {
            acc[header.key] = header.value;
          }
          return acc;
        },
        {} as Record<string, string>,
      ),
  );

export const HandoffBasicSchema = z
  .object({
    methodType: sipMethod,
    name: HandoffNameSchema,
    description: HandoffDescriptionSchema.optional(),
    method: SelectOptionSchemaTransformed,
    sipHeaders: SipHeadersSchema,
  })
  .merge(IdSchema.partial());

export const RouteSchema = z.object({
  route: HandoffPhoneNumberSchema,
});

export const InviteSchema = z.object({
  encryption: SelectOptionSchemaTransformed,
  integrationName: OutboundEndpointSchema,
});

export const ReferHandoffSchema = HandoffBasicSchema.merge(RouteSchema).merge(
  z.object({
    methodType: sipRefer,
  }),
);

export const InviteHandoffSchema = HandoffBasicSchema.merge(InviteSchema)
  .merge(RouteSchema)
  .merge(
    z.object({
      methodType: sipInvite,
    }),
  );

export const ByeSchema = HandoffBasicSchema.merge(
  z.object({
    methodType: sipBye,
  }),
);

export const HandoffSchema = z.discriminatedUnion('methodType', [
  ReferHandoffSchema,
  InviteHandoffSchema,
  ByeSchema,
]);
